import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, Fragment as _Fragment, renderList as _renderList, createTextVNode as _createTextVNode, withCtx as _withCtx, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "py-12 xl:container xl:mx-auto xl:px-4" }
const _hoisted_2 = { class: "px-4 font-body text-2xl font-semibold xl:px-0" }
const _hoisted_3 = { class: "mb-16" }
const _hoisted_4 = { class: "px-4 xl:px-0" }
const _hoisted_5 = {
  key: 1,
  class: "mt-6 mb-2 flex items-center"
}
const _hoisted_6 = { class: "ml-2 text-xl" }
const _hoisted_7 = { class: "mb-16" }
const _hoisted_8 = { class: "mt-8 mb-3 px-4 text-xl xl:px-0" }
const _hoisted_9 = {
  key: 1,
  class: "mt-8 px-4 text-xl xl:px-0"
}
const _hoisted_10 = { class: "mb-16" }
const _hoisted_11 = { class: "mt-4 flex px-4 xl:px-0" }
const _hoisted_12 = { class: "mb-2 text-base" }
const _hoisted_13 = { class: "mb-16 px-4 xl:px-0" }
const _hoisted_14 = { class: "mt-8 font-body text-2xl font-semibold" }
const _hoisted_15 = ["src", "alt"]
const _hoisted_16 = {
  key: 5,
  class: "px-4 xl:px-0"
}
const _hoisted_17 = {
  class: /*#__PURE__*/_normalizeClass(['mt-8 font-body text-2xl font-semibold'])
}

import { getAddress } from '@ethersproject/address';
import { formatUnits } from 'ethers/lib/utils';
import { computed, onBeforeMount, watch } from 'vue';

import HeroClaim from '@/components/contextual/pages/claim/HeroClaim.vue';
import LegacyClaims from '@/components/contextual/pages/claim/LegacyClaims.vue';
import BalClaimsTable, {
  RewardRow,
} from '@/components/tables/BalClaimsTable.vue';
import GaugeRewardsTable from '@/components/tables/GaugeRewardsTable.vue';
import ProtocolRewardsTable, {
  ProtocolRewardRow,
} from '@/components/tables/ProtocolRewardsTable.vue';
import useApp from '@/composables/useApp';
import { GaugePool, useClaimsData } from '@/composables/useClaimsData';
import { isL2, isMainnet } from '@/composables/useNetwork';
import useNumbers from '@/composables/useNumbers';
import { isStableLike } from '@/composables/usePool';
import { useTokenHelpers } from '@/composables/useTokenHelpers';
import useTokens from '@/composables/useTokens';
import { FiatCurrency } from '@/constants/currency';
import { bnum } from '@/lib/utils';
import { bbAUSDToken } from '@/services/balancer/contracts/contracts/bb-a-usd-token';
import { Gauge } from '@/services/balancer/gauges/types';
import { configService } from '@/services/config/config.service';
import { BalanceMap } from '@/services/token/concerns/balances.concern';
import useWeb3 from '@/services/web3/useWeb3';

/**
 * TYPES
 */
type GaugeTable = {
  gauge: Gauge;
  pool: GaugePool;
};

/**
 * COMPOSABLES
 */

export default _defineComponent({
  setup(__props) {

const { injectTokens, injectPrices, getToken } = useTokens();
const { balToken } = useTokenHelpers();
const { toFiat, fNum2 } = useNumbers();
const { isWalletReady } = useWeb3();
const { appLoading } = useApp();
const {
  gauges,
  gaugePools,
  protocolRewards,
  isLoading: isClaimsLoading,
} = useClaimsData();

/**
 * STATE
 */
const networks = [
  {
    id: 'ethereum',
    name: 'Ethereum',
    subdomain: 'app',
    key: '1',
  },
  {
    id: 'polygon',
    name: 'Polygon',
    subdomain: 'polygon',
    key: '137',
  },
  {
    id: 'arbitrum',
    name: 'Arbitrum',
    subdomain: 'arbitrum',
    key: '42161',
  },
];

/**
 * COMPUTED
 */
const loading = computed(
  (): boolean =>
    (isClaimsLoading.value || appLoading.value) && isWalletReady.value
);

const networkBtns = computed(() => {
  return networks.filter(network => network.key !== configService.network.key);
});

const balRewardsData = computed((): RewardRow[] => {
  if (!isWalletReady.value || appLoading.value) return [];
  // Using reduce to filter out gauges we don't have corresponding pools for
  return gauges.value.reduce<RewardRow[]>((arr, gauge) => {
    const amount = formatUnits(gauge.claimableTokens, balToken.value.decimals);
    const pool = gaugePools.value.find(pool => pool.id === gauge.poolId);

    if (pool && bnum(amount).gt(0))
      arr.push({
        gauge,
        pool,
        amount,
        value: toFiat(amount, balToken.value.address),
      });

    return arr;
  }, []);
});

const protocolRewardsData = computed((): ProtocolRewardRow[] => {
  return formatRewardsData(protocolRewards.value.v2);
});

/**
 * The feeDistributor contract was updated and so we need to support the old
 * one so that users can claim their rewards. Eventually we should be able to
 * remove this.
 */
const protocolRewardsDataDeprecated = computed((): ProtocolRewardRow[] => {
  return formatRewardsData(protocolRewards.value.v1);
});

const gaugesWithRewards = computed((): Gauge[] => {
  return gauges.value.filter(gauge => gauge.rewardTokens.length > 0);
});

const gaugeTables = computed((): GaugeTable[] => {
  // Only return gauges if we have a corresponding pool and rewards > 0
  return gaugesWithRewards.value.reduce<GaugeTable[]>((arr, gauge) => {
    const pool = gaugePools.value.find(pool => pool.id === gauge.poolId);
    const totalRewardValue = Object.values(gauge.claimableRewards).reduce(
      (acc, reward) => acc.plus(reward),
      bnum(0)
    );

    if (pool && totalRewardValue.gt(0))
      arr.push({
        gauge,
        pool,
      });

    return arr;
  }, []);
});

/**
 * METHODS
 */
async function injectRewardTokens(gauges: Gauge[]): Promise<void> {
  const allRewardTokens = gauges.map(gauge => gauge.rewardTokens).flat();
  return await injectTokens(allRewardTokens);
}

async function injectPoolTokens(pools: GaugePool[]): Promise<void> {
  const allPoolTokens = pools.map(pools => pools.tokensList).flat();
  return await injectTokens(allPoolTokens);
}

function gaugeTitle(pool: GaugePool): string {
  const _tokens = pool.tokens.map(token => ({
    ...token,
    ...getToken(getAddress(token.address)),
  }));

  if (isStableLike(pool.poolType)) {
    return Object.values(_tokens)
      .map(token => token.symbol)
      .join(' / ');
  }

  return Object.values(_tokens)
    .map(
      token =>
        `${fNum2(token.weight, {
          style: 'percent',
          maximumFractionDigits: 0,
        })} ${token.symbol}`
    )
    .join(' / ');
}

function formatRewardsData(data?: BalanceMap): ProtocolRewardRow[] {
  if (!isWalletReady.value || appLoading.value || !data) return [];

  return Object.keys(data).map(tokenAddress => {
    const token = getToken(tokenAddress);
    const amount = formatUnits(data[tokenAddress], token.decimals);

    return {
      token,
      amount,
      value: toFiat(amount, tokenAddress),
    };
  });
}

/**
 * @summary Fetches bb-a-USD rate as an appoximation of USD price.
 */
async function getBBaUSDPrice() {
  if (isMainnet.value) {
    const appoxPrice = await bbAUSDToken.getRate();
    injectPrices({
      [FiatCurrency.usd]: {
        [bbAUSDToken.address as string]: bnum(appoxPrice).toNumber(),
      },
    });
  }
}

/**
 * WATCHERS
 */
watch(gauges, async newGauges => {
  if (newGauges) await injectRewardTokens(newGauges);
});

watch(gaugePools, async newPools => {
  if (newPools) await injectPoolTokens(newPools);
});

/**
 * LIFECYCLE
 */
onBeforeMount(async () => {
  if (!isL2.value) await getBBaUSDPrice();
});

return (_ctx: any,_cache: any) => {
  const _component_BalLoadingBlock = _resolveComponent("BalLoadingBlock")!
  const _component_BalAsset = _resolveComponent("BalAsset")!
  const _component_BalBlankSlate = _resolveComponent("BalBlankSlate")!
  const _component_BalBtn = _resolveComponent("BalBtn")!
  const _component_BalFlexGrid = _resolveComponent("BalFlexGrid")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(HeroClaim),
    _createElementVNode("div", null, [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("h2", _hoisted_2, _toDisplayString(_unref(configService).network.chainName) + " " + _toDisplayString(_ctx.$t('liquidityIncentives')), 1),
        (!_unref(isL2))
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, [
                  (_unref(appLoading))
                    ? (_openBlock(), _createBlock(_component_BalLoadingBlock, {
                        key: 0,
                        class: "mt-6 mb-2 h-8 w-64"
                      }))
                    : (_openBlock(), _createElementBlock("div", _hoisted_5, [
                        _createVNode(_component_BalAsset, {
                          address: _unref(balToken)?.address
                        }, null, 8, ["address"]),
                        _createElementVNode("h3", _hoisted_6, " Balancer (BAL) " + _toDisplayString(_ctx.$t('earnings').toLowerCase()), 1)
                      ]))
                ]),
                _createVNode(BalClaimsTable, {
                  rewardsData: _unref(balRewardsData),
                  isLoading: _unref(loading)
                }, null, 8, ["rewardsData", "isLoading"])
              ]),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("h3", _hoisted_8, _toDisplayString(_ctx.$t('protocolEarnings')), 1),
                _createVNode(ProtocolRewardsTable, {
                  rewardsData: _unref(protocolRewardsData),
                  isLoading: _unref(loading)
                }, null, 8, ["rewardsData", "isLoading"]),
                (!_unref(loading))
                  ? (_openBlock(), _createBlock(ProtocolRewardsTable, {
                      key: 0,
                      rewardsData: _unref(protocolRewardsDataDeprecated),
                      isLoading: _unref(loading),
                      deprecated: ""
                    }, null, 8, ["rewardsData", "isLoading"]))
                  : _createCommentVNode("", true)
              ])
            ], 64))
          : _createCommentVNode("", true),
        (!_unref(isL2))
          ? (_openBlock(), _createElementBlock("h3", _hoisted_9, _toDisplayString(_ctx.$t('otherTokenEarnings')), 1))
          : _createCommentVNode("", true),
        (_unref(loading))
          ? (_openBlock(), _createBlock(_component_BalLoadingBlock, {
              key: 2,
              class: "mt-6 mb-2 h-56"
            }))
          : _createCommentVNode("", true),
        (!_unref(isClaimsLoading) && !_unref(appLoading) && _unref(gaugeTables).length > 0)
          ? (_openBlock(true), _createElementBlock(_Fragment, { key: 3 }, _renderList(_unref(gaugeTables), ({ gauge, pool }) => {
              return (_openBlock(), _createElementBlock("div", {
                key: gauge.id
              }, [
                _createElementVNode("div", _hoisted_10, [
                  _createElementVNode("div", _hoisted_11, [
                    _createElementVNode("h4", _hoisted_12, _toDisplayString(gaugeTitle(pool)), 1)
                  ]),
                  _createVNode(GaugeRewardsTable, {
                    gauge: gauge,
                    isLoading: _unref(isClaimsLoading) || _unref(appLoading)
                  }, null, 8, ["gauge", "isLoading"])
                ])
              ]))
            }), 128))
          : (
          (!_unref(isClaimsLoading) && !_unref(appLoading) && _unref(gaugeTables).length === 0) ||
          !_unref(isWalletReady)
        )
            ? (_openBlock(), _createBlock(_component_BalBlankSlate, {
                key: 4,
                class: "mt-4 mb-16 px-4 xl:px-0"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('noClaimableIncentives')), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_13, [
          _createElementVNode("h2", _hoisted_14, _toDisplayString(_ctx.$t('pages.claim.titles.incentivesOnOtherNetworks')), 1),
          _createVNode(_component_BalFlexGrid, {
            class: "mt-4",
            flexWrap: ""
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(networkBtns), (network) => {
                return (_openBlock(), _createBlock(_component_BalBtn, {
                  key: network.id,
                  tag: "a",
                  href: `https://${network.subdomain}.balancer.fi/#/claim`,
                  color: "white"
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("img", {
                      src: require(`@/assets/images/icons/networks/${network.id}.svg`),
                      alt: network.id,
                      class: "mr-2 h-6 w-6 rounded-full shadow-sm"
                    }, null, 8, _hoisted_15),
                    _createTextVNode(" " + _toDisplayString(_ctx.$t('pages.claim.btns.claimOn')) + " " + _toDisplayString(network.name), 1)
                  ]),
                  _: 2
                }, 1032, ["href"]))
              }), 128))
            ]),
            _: 1
          })
        ]),
        (_unref(isWalletReady))
          ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
              _createElementVNode("h2", _hoisted_17, _toDisplayString(_ctx.$t('pages.claim.titles.legacyIncentives')), 1),
              _createVNode(LegacyClaims)
            ]))
          : _createCommentVNode("", true)
      ])
    ])
  ], 64))
}
}

})